import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import itCommon from './locales/it/common.json'
import itAuth from './locales/it/auth.json'
import itDashboard from './locales/it/dashboard.json'
import itSequences from './locales/it/sequences.json'
import itContacts from './locales/it/contacts.json'
import itEnrollments from './locales/it/enrollments.json'
import itSettings from './locales/it/settings.json'
import itErrors from './locales/it/errors.json'
import itLegal from './locales/it/legal.json'
import itFeedback from './locales/it/feedback.json'

import enCommon from './locales/en/common.json'
import enAuth from './locales/en/auth.json'
import enDashboard from './locales/en/dashboard.json'
import enSequences from './locales/en/sequences.json'
import enContacts from './locales/en/contacts.json'
import enEnrollments from './locales/en/enrollments.json'
import enSettings from './locales/en/settings.json'
import enErrors from './locales/en/errors.json'
import enLegal from './locales/en/legal.json'
import enFeedback from './locales/en/feedback.json'

export const NAMESPACES = [
  'common', 'auth', 'dashboard', 'sequences',
  'contacts', 'enrollments', 'settings', 'errors', 'legal', 'feedback',
] as const

// Single source of truth for the supported languages. Adding a language here
// (plus its locale and resource bundle) flows through to i18next's supportedLngs,
// the LanguageSelector dropdown, and the locale-aware formatters in lib/format.ts.
export const SUPPORTED_LANGS = ['it', 'en'] as const
export type Lang = (typeof SUPPORTED_LANGS)[number]
export const LOCALES: Record<Lang, string> = { it: 'it-IT', en: 'en-US' }

/** Resolve the active language to one of SUPPORTED_LANGS (tolerates region suffixes like "en-US"). */
export function resolveLang(): Lang {
  return SUPPORTED_LANGS.find((lang) => i18n.language?.startsWith(lang)) ?? 'it'
}

// Set while a language the user picked on this browser is still to be saved
// on their profile (#346). The detector's own `language` key cannot tell: it
// also caches the fallback language of a browser where nobody ever chose.
const LANGUAGE_CHOICE_KEY = 'language_choice_pending'

/** The user picks a language: unlike the fallback, it has to reach their profile. */
export function chooseLanguage(lang: Lang) {
  try {
    localStorage.setItem(LANGUAGE_CHOICE_KEY, '1')
  } catch {
    // Without storage the choice still applies to this page
  }
  return i18n.changeLanguage(lang)
}

export function hasPendingLanguageChoice(): boolean {
  try {
    return localStorage.getItem(LANGUAGE_CHOICE_KEY) !== null
  } catch {
    return false
  }
}

export function clearPendingLanguageChoice() {
  try {
    localStorage.removeItem(LANGUAGE_CHOICE_KEY)
  } catch {
    // Nothing to clear
  }
}

export const resources = {
  it: {
    common: itCommon, auth: itAuth, dashboard: itDashboard, sequences: itSequences,
    contacts: itContacts, enrollments: itEnrollments, settings: itSettings, errors: itErrors,
    legal: itLegal, feedback: itFeedback,
  },
  en: {
    common: enCommon, auth: enAuth, dashboard: enDashboard, sequences: enSequences,
    contacts: enContacts, enrollments: enEnrollments, settings: enSettings, errors: enErrors,
    legal: enLegal, feedback: enFeedback,
  },
} as const

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'it',
    supportedLngs: [...SUPPORTED_LANGS],
    defaultNS: 'common',
    ns: [...NAMESPACES],
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'language',
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  })

export default i18n
